import { readFile } from "node:fs/promises"
import path from "node:path"

import { collectFiles, toRelative, uniqueSorted } from "./helpers.js"
import { ComponentDescriptor, StoryRecord } from "./types.js"

const storiesPattern = "**/*.stories.{ts,tsx,js,jsx,mdx}"

function inferCandidatesFromFile(storyPath: string): string[] {
  const fileName = path.basename(storyPath)
  const [baseName] = fileName.split(".stories")
  return uniqueSorted([baseName, baseName.replaceAll(/[-_]+(.)/g, (_, c: string) => c.toUpperCase())])
}

function inferCandidatesFromMeta(content: string): string[] {
  const titleMatch = content.match(/title\s*:\s*["']([^"']+)["']/)
  if (!titleMatch?.[1]) {
    return []
  }

  const parts = titleMatch[1].split("/").filter(Boolean)
  const maybeComponent = parts.at(-1)
  if (!maybeComponent) {
    return []
  }

  return [maybeComponent]
}

function lineAt(content: string, index: number): number {
  return content.slice(0, index).split("\n").length
}

function extractStoryExports(content: string): Array<{ lineEnd: number; lineStart: number; snippet: string; storyName: string }> {
  const exports = [...content.matchAll(/export\s+(?:const|function)\s+([A-Za-z0-9_]+)/g)].filter(
    match => match[1] !== "default",
  )

  return exports
    .map((match, index) => {
      const storyName = match[1]
      const start = match.index ?? 0
      const nextStart = exports[index + 1]?.index
      const endExclusive = typeof nextStart === "number" ? nextStart : content.length
      const snippet = content.slice(start, endExclusive).trim()

      return {
        lineEnd: lineAt(content, Math.max(start, endExclusive - 1)),
        lineStart: lineAt(content, start),
        snippet,
        storyName,
      }
    })
    .sort((a, b) => a.storyName.localeCompare(b.storyName))
}

export async function analyzeStorybookStories(
  projectRoot: string,
  components: ComponentDescriptor[],
  excludePaths: string[] = [],
): Promise<Record<string, StoryRecord[]>> {
  const files = await collectFiles({
    excludePaths,
    pattern: storiesPattern,
    projectRoot,
  })
  const byExportName = new Map(components.map(component => [component.exportName.toLowerCase(), component]))
  const records: Record<string, StoryRecord[]> = {}

  for (const file of files) {
    const content = await readFile(file, "utf8")
    const stories = extractStoryExports(content)

    if (stories.length === 0) {
      continue
    }

    const relativePath = toRelative(projectRoot, file)
    const candidates = uniqueSorted([...inferCandidatesFromFile(relativePath), ...inferCandidatesFromMeta(content)])

    for (const candidate of candidates) {
      const component = byExportName.get(candidate.toLowerCase())
      if (!component) {
        continue
      }

      if (!records[component.componentKey]) {
        records[component.componentKey] = []
      }

      for (const story of stories) {
        records[component.componentKey].push({
          lineEnd: story.lineEnd,
          lineStart: story.lineStart,
          snippet: story.snippet,
          storyName: story.storyName,
          storyPath: relativePath,
        })
      }
    }
  }

  for (const componentKey of Object.keys(records)) {
    records[componentKey] = records[componentKey].sort((a, b) => {
      if (a.storyPath === b.storyPath) {
        return a.lineStart - b.lineStart
      }

      return a.storyPath.localeCompare(b.storyPath)
    })
  }

  return records
}
